import {
  Divider,
  Modal,
  Space,
  Table,
  Tag,
  Input,
  TablePaginationConfig,
} from 'antd'
import React, { useEffect, useState } from 'react'
import { EyeOutlined } from '@ant-design/icons'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import usePage from 'src/hooks/usePage'
import { search, usersAccessTypeMetrics, updateUserPoints } from 'src/api/user'
import UserSearch from './search'
import { Link } from 'react-router-dom'
import { mncmccOptions } from 'src/constants'
import { fetchUserInfo } from 'src/api/authen'
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons'
import { Button, message } from 'antd'
import { SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface'
import TextArea from 'antd/lib/input/TextArea'
const { Column } = Table

interface User {
  id: string
  username: string
  firstName?: string
  lastName?: string
  createdOn?: string
  phoneNumber?: {
    countryCode?: string
    number?: string
  }
  email?: string
  gender?: string
  dateOfBirth?: string
  points?: number
  providerCountry?: string
  userId?: string
  paymentSource?: string
  userName?: string
}

const UserPage: React.FC = () => {
  const {
    pageData,
    onQuery,
    appendSearchForm,
    submitSearchForm,
    fetchList,
    queryObj,
    searchForm,
  } = usePage({
    getData: search,
    searchData: search,
    searchType: 'POST',
  })
  const [metricsData, setMetricsData] = useState(null)
  const [pointsUser, setPointsUser] = useState(false)
  const [pointsModalOpen, setPointsModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [updatedPoints, setUpdatedPoints] = useState<number | null>(null)
  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState('')
  const [updating, setUpdating] = useState(false)

  type SortItem = { field: string; order: 'asc' | 'desc' }

  const openPointsModal = (user: User) => {
    setSelectedUser(user)
    setUpdatedPoints(user.points ?? 0)
    setReason('')
    setReasonError('')
    setPointsModalOpen(true)
  }

  const [redeemUser, setRedeemUser] = useState(false)

  const fetchUserAccess = async () => {
    try {
      const { data } = await fetchUserInfo()
      setPointsUser(
        data != null && data.permissions != null
          ? data.permissions.includes('user-points-view') ||
              data.permissions.includes('superadmin')
          : false
      )
      setRedeemUser(
        data != null &&
          (data.permissions.includes('superadmin') ||
            data?.accessToUserInfo === true)
      )
    } catch (error) {
      console.error('Error fetching user access data:', error)
    }
  }

  const PointsPill = ({ record, onClick, canEdit }) => {
    return (
      <div
        onClick={() => canEdit && onClick(record)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 12px',
          borderRadius: 8,
          background: '#1f1f1f',
          cursor: canEdit ? 'pointer' : 'default',
          minWidth: 80,
          justifyContent: 'space-between',
          opacity: canEdit ? 1 : 0.8,
        }}
      >
        <span style={{ fontWeight: 500 }}>{record.points ?? 0}</span>

        {canEdit && (
          <span style={{ display: 'flex', gap: 4, opacity: 0.7 }}>
            <ArrowUpOutlined />
            <ArrowDownOutlined />
          </span>
        )}
      </div>
    )
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, (string | number | boolean)[] | null>,
    sorter: SorterResult<User> | SorterResult<User>[],
    _extra: TableCurrentDataSource<User>
  ) => {
    const current = pagination.current ?? 1
    const pageSize = pagination.pageSize ?? 10

    const sorterArr: SorterResult<User>[] = Array.isArray(sorter)
      ? sorter
      : [sorter]

    const sorts: SortItem[] = sorterArr
      .filter((s) => !!s && !!s.order && !!s.field)
      .map((s) => ({
        field: String(s.field),
        order: s.order === 'ascend' ? 'asc' : 'desc',
      }))

    onQuery({
      page: current - 1,
      size: pageSize,
      sorts: JSON.stringify(sorts),
    })
  }
  const mncmccMap = Object.fromEntries(
    mncmccOptions.map(({ value, label }) => [value, label])
  )

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const data = await usersAccessTypeMetrics(searchForm.current)
        setMetricsData(data)
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }
    fetchMetrics()
  }, [queryObj])

  useEffect(() => {
    fetchUserAccess()
  }, [])

  const handleUpdatePoints = async () => {
    if (!reason.trim()) {
      setReasonError('Reason is required')
      return
    }

    if (!selectedUser || updatedPoints === null) {
      message.error('Please enter valid points')
      return
    }

    try {
      setUpdating(true)
      await updateUserPoints(selectedUser, updatedPoints, reason)
      setPointsModalOpen(false)

      const currentPage = parseInt(pageData.query.page) || 0
      const pageSize = parseInt(pageData.query.pageSize) || 10
      const currentSorts = queryObj.sorts || ''

      await fetchList()

      onQuery({
        page: currentPage.toString(),
        size: pageSize.toString(),
        sorts: currentSorts,
      })
      const metrics = await usersAccessTypeMetrics(searchForm.current)
      setMetricsData(metrics)
    } catch (error) {
      message.error('Failed to update points. Please try again.')
    } finally {
      setUpdating(false)
    }
  }

  return (
    <>
      <Divider />
      <DndProvider backend={HTML5Backend}>
        <Table<User>
          className="table-sorting"
          rowKey="id"
          dataSource={pageData.data}
          loading={pageData.loading}
          onChange={handleTableChange}
          pagination={{
            current: parseInt(pageData.query.page) + 1,
            total: pageData.total,
            pageSize: parseInt(pageData.query.pageSize) || 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            onChange: (p, ps) =>
              onQuery({ page: (p - 1).toString(), pageSize: ps.toString() }),
          }}
          scroll={{
            x: 1000,
          }}
          title={() => (
            <UserSearch
              appendSearchForm={appendSearchForm}
              submitSearchForm={submitSearchForm}
            />
          )}
          footer={() => (
            <div
              style={{
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
              }}
            >
              <span>Total Users: {pageData.total}</span>
              <span>Premium: {metricsData?.premiumCount}</span>
              <span>Freemium: {metricsData?.freemiumCount}</span>
              <span>Guest: {metricsData?.guestCount}</span>
            </div>
          )}
        >
          <Column<User>
            dataIndex="userName"
            title="User Name"
            sorter={{ multiple: 2 }}
          />
          <Column<User>
            dataIndex="userId"
            title="User Id"
            sorter={{ multiple: 1 }}
          />
          {redeemUser && (
            <Column<User>
              title="Name"
              render={(text, record) => {
                const firstName = record.firstName
                const lastName = record.lastName
                return firstName && lastName
                  ? `${firstName} ${lastName}`.trim()
                  : ''
              }}
              dataIndex="firstName"
              sorter={{ multiple: 1 }}
            />
          )}

          <Column
            dataIndex="accessLevel"
            title="User Type"
            render={(text) => {
              const colorStyle: React.CSSProperties = {
                color: 'white',
                textAlign: 'center',
                width: 100,
                display: 'inline-block',
                borderRadius: '4px',
                padding: '0 8px',
              }

              switch (text) {
                case 'FREEMIUM':
                  colorStyle.backgroundColor = 'rgba(158, 54, 187, 0.78)'
                  colorStyle.color = 'rgba(222, 222, 222, 1)'
                  break
                case 'GUEST':
                  colorStyle.backgroundColor = 'rgba(54, 187, 156, 0.78)'
                  colorStyle.color = 'rgba(222, 222, 222, 1)'
                  break
                case 'SUBSCRIBED':
                  colorStyle.backgroundColor = 'rgba(187, 54, 132, 0.78)'
                  colorStyle.color = 'rgba(222, 222, 222, 1)'
                  break
                default:
                  colorStyle.backgroundColor = 'rgba(217, 217, 217, 0.78)'
                  colorStyle.color = 'rgba(222, 222, 222, 1)'
              }

              return <Tag style={colorStyle}>{text}</Tag>
            }}
            sorter={{ multiple: 1 }}
          />
          <Column<User>
            title="Joined On"
            dataIndex="createdOn"
            sorter={{ multiple: 5 }}
            render={(text, record) => {
              const dateString = record.createdOn
              if (dateString) {
                const date = new Date(dateString)
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                return `${year}-${month}-${day}`
              }
              return ''
            }}
          />
          {redeemUser && (
            <Column<User>
              title="Phone Number"
              dataIndex="phoneNumber"
              sorter={{ multiple: 1 }}
            />
          )}
          {redeemUser && (
            <Column<User>
              dataIndex="email"
              title="Email"
              sorter={{ multiple: 1 }}
            />
          )}
          <Column<User>
            dataIndex="gender"
            title="Gender"
            sorter={{ multiple: 1 }}
          />
          {redeemUser && (
            <Column<User>
              dataIndex="dateOfBirth"
              title="Date Of Birth"
              render={(text, record) => {
                const dateString = record.dateOfBirth
                if (dateString) {
                  const date = new Date(dateString)
                  const year = date.getFullYear()
                  const month = String(date.getMonth() + 1).padStart(2, '0')
                  const day = String(date.getDate()).padStart(2, '0')
                  return `${year}-${month}-${day}`
                }
                return ''
              }}
              sorter={{ multiple: 1 }}
            />
          )}
          <Column<User>
            title="Total Points"
            dataIndex="points"
            sorter={{ multiple: 3 }}
            render={(_, record) => (
              <PointsPill
                record={record}
                onClick={openPointsModal}
                canEdit={pointsUser}
              />
            )}
          />
          <Column<User>
            dataIndex="providerCountry"
            title="Country Provider"
            sorter={{ multiple: 1 }}
          />
          <Column<User>
            title="Payment Source"
            dataIndex="paymentSource"
            sorter={{ multiple: 1 }}
          />
          <Column<User>
            title="Source"
            dataIndex="mncmcc"
            render={(value: string) => mncmccMap[value] || value}
            sorter={{ multiple: 1 }}
          />
          <Column<{ userName: string }>
            title="Action"
            key="url"
            render={(_, record) => (
              <Space size="middle">
                <Link
                  to={`/users/detail/${record.userName}`}
                  className="link-btn"
                >
                  <EyeOutlined />
                </Link>
              </Space>
            )}
          />
        </Table>
      </DndProvider>
      <Modal
        title="Update Points"
        visible={pointsModalOpen}
        onCancel={() => setPointsModalOpen(false)}
        footer={null}
        centered
      >
        {selectedUser && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <strong>Username:</strong> {selectedUser.userName}
            </div>

            <div>
              <strong>Current Points:</strong> {selectedUser.points ?? 0}
            </div>

            <div>
              <strong>New Points:</strong>
              <Input
                type="text"
                value={updatedPoints ?? ''}
                inputMode="numeric"
                pattern="[0-9]*"
                onChange={(e) =>
                  setUpdatedPoints(Number(e.target.value.replace(/\D/g, '')))
                }
                style={{
                  width: '100%',
                  marginTop: 6,
                  padding: '8px 10px',
                  borderRadius: 6,
                  background: '#1f1f1f',
                  border: '1px solid #303030',
                  color: '#fff',
                }}
              />
            </div>

            <div>
              <strong>Reason for Update: *</strong>
              <TextArea
                style={{
                  marginTop: 6,
                  borderColor: reasonError ? '#ff4d4f' : undefined,
                }}
                placeholder="Enter reason for updating points (required)"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value)
                  setReasonError('')
                }}
                rows={3}
              />
              {reasonError && (
                <div
                  style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 4 }}
                >
                  {reasonError}
                </div>
              )}
            </div>

            <div style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                loading={updating}
                onClick={handleUpdatePoints}
                style={{
                  background:
                    'linear-gradient(90deg, #5b2be0 0%, #d6008f 100%)',
                  border: 'none',
                  borderRadius: 8,
                  padding: '0 24px',
                }}
              >
                Update
              </Button>
            </div>
          </Space>
        )}
      </Modal>
    </>
  )
}

UserPage.displayName = 'UserPage'
export default UserPage
